import { ComplexValue, Value } from 'app/types-shared';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { DefaultFilterType } from '../constants';
import { DefaultFilterWire, DefaultFilterMetadataWire, DefaultFilterConfig } from '../types';
import { DefaultFilter, FilterState } from '../types-store';

const mapDomain = (domainWire: Value, filterMetadata: DefaultFilterMetadataWire): ComplexValue<number> => {

  const domain = codeItemToStoreValue(domainWire);
  const description = filterMetadata?.domain?.d;

  if (!domain || !description || domain.description === description) {
    return domain;
  }

  return {
    ...domain,
    description
  };
};

const mapDefaultValue = (defaultValue: Value, filterMetadata: DefaultFilterMetadataWire): ComplexValue<number> => {

  return isDefinedIncludingZero(defaultValue?.v) &&
    (defaultValue?.d || filterMetadata?.advancedSearchType === DefaultFilterType.Checkbox)
    ? codeItemToStoreValue(defaultValue)
    : undefined;
};

const mapState = (filter: DefaultFilterWire, filterMetadata: DefaultFilterMetadataWire): FilterState => {

  const advancedSearchType = filterMetadata?.advancedSearchType;

  return filter.defaultValue?.v || filter.defaultValue2?.v || filter.myValue
    ? advancedSearchType === DefaultFilterType.Date
      ? FilterState.Range
      : FilterState.DefaultValue
    : filter.periodPredef?.v
      ? FilterState.FixedPeriod
      : FilterState.NoDefaultValue;
};

const sortFilters = (filter1: DefaultFilter, filter2: DefaultFilter): number => {
  const filter1HasSequence = !isDefinedIncludingZero(filter1.sequence) ? 1 : 0;
  const filter2HasSequence = !isDefinedIncludingZero(filter2.sequence) ? 1 : 0;

  return filter1HasSequence - filter2HasSequence !== 0
    ? filter1HasSequence - filter2HasSequence
    : (filter1.sequence ?? 0) - (filter2.sequence ?? 0) !== 0
      ? (filter1.sequence ?? 0) - (filter2.sequence ?? 0)
      : (filter1.domain?.description ?? '').localeCompare(filter2.domain?.description ?? '');
};

export const mapFiltersFromWire = (
  filters: DefaultFilterWire[],
  filterMetadataWire: DefaultFilterMetadataWire[],
  config: DefaultFilterConfig
): DefaultFilter[] =>
  filters?.map(filter => {
    const filterMetadata = filterMetadataWire
      ?.find(metadata => metadata.domain?.v === filter.domain?.v);

    return {
      domain: mapDomain(filter.domain, filterMetadata),
      required: !!(config?.showRequired && filter.required),
      sequence: filter.sequence,
      myValue: filter.myValue,
      defaultValue: mapDefaultValue(filter.defaultValue, filterMetadata),
      defaultValue2: codeItemToStoreValue(filter.defaultValue2),
      periodPredef: codeItemToStoreValue(filter.periodPredef),
      state: mapState(filter, filterMetadata)
    };
  })
    ?.sort(sortFilters) ?? [];

export const mapFiltersToWire = (filters: DefaultFilter[], config: DefaultFilterConfig): DefaultFilterWire[] =>
  filters?.map(filter => ({
    domain: { v: filter.domain?.value },
    ...(!!config?.showRequired && { required: filter.required }),
    sequence: filter.sequence,
    ...(filter.myValue !== undefined && { myValue: filter.myValue }),
    ...(!!filter.defaultValue && { defaultValue: { v: filter.defaultValue?.value } }),
    ...(!!filter.defaultValue2 && { defaultValue2: { v: filter.defaultValue2?.value } }),
    ...(!!filter.periodPredef && { periodPredef: { v: filter.periodPredef?.value } })
  })
  ) ?? [];
